import { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  MonitorSmartphone,
  Video,
  Bell,
  BarChart3,
  Settings,
  PanelLeftClose,
  PanelLeftOpen,
  UserCircle,
  Users,
  HelpCircle,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useUIStore } from '@/stores/uiStore';
import { Tooltip } from '@/components/ui/Tooltip';
import faviconIcon from '@/components/images/favicon.png';
import './Sidebar.scss';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

interface NavGroup {
  label: string;
  icon: LucideIcon;
  children: NavItem[];
}

type NavEntry = NavItem | NavGroup;

const NAV: NavEntry[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/devices',   label: 'Devices',   icon: MonitorSmartphone },
  { to: '/cameras',   label: 'Cameras',   icon: Video },
  { to: '/alerts',    label: 'Alerts',    icon: Bell },
  // Analytics — un-hidden 2026-06-22 for MVP 2.0 (Overview ships with the
  // /api/analytics/overview endpoint; deeper pages follow in P1+).
  { to: '/analytics', label: 'Analytics', icon: BarChart3 },
  {
    label: 'Config',
    icon: Settings,
    // Configuration surfaces. Keeping these under a single group keeps the
    // primary nav focused on operations (live ops) and pushes setup/admin
    // into its own visual cluster — same pattern Linear/Stripe use.
    children: [
      // "Camera settings" reads as the destination ("manage camera config")
      // rather than the noun "Cameras" which collides with the top-level
      // /cameras (live view) route. Keeps the URL stable; only the label
      // and the in-page heading change.
      { to: '/config/cameras', label: 'Camera settings', icon: Video },
      // Face enrollment hub — Phase 1 surfaces groups + persons. Lives
      // under Config because operators set it up once and rarely revisit
      // (vs Dashboard / Alerts which are daily ops surfaces). Recognition
      // is bound per-zone in Camera settings — same architectural shape
      // as the AI types catalog.
      { to: '/faces', label: 'Face Recognition', icon: Users },
      // Profile — account details + the Telegram alert destination
      // (`telegramGroupName` → resolved chat_id, shared-bot flow). Lives
      // under Config because it's a setup surface, not an operational one.
      { to: '/profile', label: 'Profile', icon: UserCircle },
    ],
  },
];

const isGroup = (entry: NavEntry): entry is NavGroup => 'children' in entry;

export const Sidebar = () => {
  const mobileOpen = useUIStore((s) => s.mobileSidebarOpen);
  const closeMobile = useUIStore((s) => s.closeMobileSidebar);
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const toggleCollapsed = useUIStore((s) => s.toggleSidebarCollapsed);
  const openGuide = useUIStore((s) => s.openGuide);
  const location = useLocation();

  // Auto-close mobile drawer on navigation.
  useEffect(() => {
    if (mobileOpen) closeMobile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Lock body scroll while mobile drawer is open.
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  return (
    <>
      <div
        className={`sidebar__backdrop${mobileOpen ? ' sidebar__backdrop--open' : ''}`}
        onClick={closeMobile}
        aria-hidden="true"
      />

      <aside
        className={[
          'sidebar',
          collapsed && 'sidebar--collapsed',
          mobileOpen && 'sidebar--mobile-open',
        ].filter(Boolean).join(' ')}
        aria-label="Primary navigation"
      >
        <div className="sidebar__brand">
          {/* Square favicon + separate text wordmark. The favicon stays the
              same size in both states; the wordmark text hides when the
              sidebar is collapsed. Cleaner than clipping a wide PNG and
              leaves the collapse toggle visually distinct from the brand. */}
          <span className="sidebar__brand-mark">
            <img src={faviconIcon} alt="ProWatchly" className="sidebar__brand-icon" />
            <span className="sidebar__brand-text">ProWatchly</span>
          </span>
          {/* Desktop collapse/expand toggle. Lives in the brand row so it sits
              next to the logo when expanded; when collapsed it takes the
              brand mark's place at the top of the rail (logo hides) so the
              control stays anchored in the same screen position. Hidden on
              mobile — the mobile drawer uses the close-X below instead.

              Icon swaps between PanelLeftClose / PanelLeftOpen (lucide's
              purpose-built sidebar toggle icons) — clearer semantics than a
              rotating chevron. */}
          <Tooltip
            label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            placement="right"
          >
            <button
              type="button"
              className="sidebar__collapse"
              onClick={toggleCollapsed}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!collapsed}
            >
              {collapsed ? (
                <PanelLeftOpen size={18} aria-hidden="true" />
              ) : (
                <PanelLeftClose size={18} aria-hidden="true" />
              )}
            </button>
          </Tooltip>
          <button
            type="button"
            className="sidebar__close-mobile"
            onClick={closeMobile}
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="sidebar__nav">
          {NAV.map((entry) => {
            if (isGroup(entry)) {
              const Icon = entry.icon;
              return (
                <div className="sidebar__group" key={`group-${entry.label}`}>
                  <Tooltip
                    label={entry.label}
                    placement="right"
                    disabled={!collapsed}
                  >
                    <div className="sidebar__group-header">
                      <Icon size={14} aria-hidden="true" className="sidebar__group-icon" />
                      <span className="sidebar__group-label">{entry.label}</span>
                    </div>
                  </Tooltip>
                  {entry.children.map(({ to, label, icon: ChildIcon }) => (
                    <Tooltip
                      key={to}
                      label={label}
                      placement="right"
                      disabled={!collapsed}
                    >
                      <NavLink
                        to={to}
                        className={({ isActive }) =>
                          `sidebar__link sidebar__link--child${isActive ? ' sidebar__link--active' : ''}`
                        }
                      >
                        <ChildIcon size={18} aria-hidden="true" />
                        <span className="sidebar__link-label">{label}</span>
                      </NavLink>
                    </Tooltip>
                  ))}
                </div>
              );
            }

            const { to, label, icon: Icon } = entry;
            return (
              <Tooltip
                key={to}
                label={label}
                placement="right"
                disabled={!collapsed}
              >
                <NavLink
                  to={to}
                  className={({ isActive }) =>
                    `sidebar__link${isActive ? ' sidebar__link--active' : ''}`
                  }
                >
                  <Icon size={18} aria-hidden="true" />
                  <span className="sidebar__link-label">{label}</span>
                </NavLink>
              </Tooltip>
            );
          })}
        </nav>

        {/* Guide / Troubleshooting — opens the bilingual help modal. Lives in a
            footer below the nav (not a route), so it's a button, not a NavLink. */}
        <div className="sidebar__footer">
          <Tooltip label="Guide & Troubleshooting" placement="right" disabled={!collapsed}>
            <button
              type="button"
              className="sidebar__link sidebar__help-btn"
              onClick={openGuide}
              aria-label="Open guide and troubleshooting"
            >
              <HelpCircle size={18} aria-hidden="true" />
              <span className="sidebar__link-label">Guide &amp; Help</span>
            </button>
          </Tooltip>
        </div>

      </aside>
    </>
  );
};
